import { Box, Flex, IconButton } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useState } from 'react';
import { MdFileCopy, MdLock } from 'react-icons/md';

import { useAlert, useApi, useAutocomplete, useGuide } from '@app/hooks';
import { api } from '@app/api';

import { AutocompleteField, FieldView, Paper, SelectField, SwitchField, TextField } from '../common';
import { Form } from '../common/form/form';
import { UserFormInstance } from './types';
import { FieldComponentProps } from '../common/types';

interface UserFormProps {
  instance: Partial<UserFormInstance>;
  action?: 'create' | 'edit';
  availableUsers: EntityBase[];
  loadingUsers: boolean;
  onSubmit: (instance: Partial<unknown>) => Promise<void>;
  onCancel: () => void;
  onChange: (field: keyof UserFormInstance) => (value: any) => void;
}

const UserFormComponent = (props: UserFormProps) => {
  const {
    action,
    instance,
    availableUsers,
    loadingUsers,
    onSubmit,
    onCancel,
    onChange,
  } = props;
  const api = useApi();
  const { success } = useAlert();
  const getAutocomplete = useAutocomplete();

  const onFetchBoss = useCallback((name: string, query: Record<string, string>) => {
    const search = query.search || '';
    if (loadingUsers || search.length < 0) {
      return Promise.resolve([]);
    }

    return Promise.resolve(availableUsers.filter(({ name }) => name.includes(search)).slice(0, 10));
  }, [availableUsers, loadingUsers]);

  const generate = () => {
    const abc = 'abcdefghkmnprstuvwxyz0123456789';
    const passwordLength = 8;
    const toUpper = (s: string) => {
      return Math.random() > 0.4 ? s.toUpperCase() : s;
    };
    let password = '';
    while (!password) {
      for (let i = 0; i < passwordLength; i++) {
        password += toUpper(abc.charAt(Math.floor(Math.random() * abc.length)));
      } 
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/.test(password)) {
        password = '';
      }
    }

    onChange('password')(password);
  };

  const copy = () => {
    navigator.clipboard.writeText(instance.password);
    success('Пароль скопирован в буфер');
  };


  return <Box w='100%' paddingX={{ base: 0, sm: 2, md: 10, lg: 14 }} >
    <Paper><Box px={4} py={4}>
      <Box mb={5}>
        <FieldView title='Имя пользователя' value={instance.username} direction='row' />
      </Box>
      <Form<UserFormInstance>
        instance={instance} 
        onSubmit={onSubmit} 
        onCancel={onCancel}  
        onChange={onChange}
        submitText={action === 'edit' ? 'Изменить' : 'Создать'}
      >
        <Form.Item<UserFormInstance, string> 
          field='email'
          label='Email'
          required
          component={TextField}
          validation={{
            message: 'Такой email уже используется',
            validation: async (value: string) => action !== 'create' || (await api.validate.get({ name: 'user_email', value }))?.data
          }}
        />
        <Form.Item<UserFormInstance, string> 
          field='phone'
          label='Телефон'
          component={TextField}
        />
        <Form.Item<UserFormInstance, string> 
          field='first_name'
          label='Имя'
          required
          component={TextField}
        />
        <Form.Item<UserFormInstance, string> 
          field='last_name'
          label='Фамилия'
          required
          component={TextField}
        />
        <Form.Item<UserFormInstance, string> 
          field='password'
          label='Пароль'
          required={action === 'create'}
          validation={{
            message: 'Пароль не удовлетворяет требованиям',
            validation: (value: string) => (action !== 'create' && !value) || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/.test(value)
          }}
          component={TextField}
        />
        <Form.Spacer >
          <Flex alignContent="center" justify='flex-start' gap={5} height="100%"  >
            <Box>
              <IconButton onClick={generate} aria-label="generate" icon={<MdLock />} />
            </Box>
            <Box>
              <IconButton onClick={copy} aria-label="copy" icon={<MdFileCopy  />} />
            </Box>
            <Box fontSize="sm" flex={1} maxW={300}>
              от 6 до 20 символов, символы в верхнем и нижнем регистре, цифры
            </Box>
          </Flex>
        </Form.Spacer>
        <Form.Divider />
        <Form.Item<UserFormInstance, EntityBase> 
          field='role'
          label='Роль'
          required
          component={SelectField}
          componentProps={{
            onFetchOptions: useGuide('user_role')
          }}
          span={{ md: 4 }}
        />
        <Form.Item<UserFormInstance, EntityBase> 
          field='group'
          label='Группа'
          required
          component={SelectField}
          componentProps={{
            onFetchOptions: useGuide('user_group')
          }}
          span={{ md: 4 }}
        />
        <Form.Item<UserFormInstance, EntityBase> 
          field='position'
          label='Должность'
          required
          component={SelectField}
          componentProps={{
            onFetchOptions: useGuide('user_position')
          }}
          span={{ md: 4 }}
        />
        <Form.Divider />
        {/* <Form.Item<UserFormInstance, EntityBase> 
          field='distributor'
          label='Дистрибьютор'
          component={AutocompleteField}
          componentProps={{
            name: 'distributor',
            onFetchOptions: getAutocomplete
          }}
        />
        <Form.Item<UserFormInstance, string> 
          field='location'
          label='Локация'
          component={TextField}
        /> */}
        <Form.Item<UserFormInstance, EntityBase> 
          field='boss'
          label='Руководитель'
          component={AutocompleteField}
          componentProps={{
            onFetchOptions: onFetchBoss,
          }}
          span={{ md: 8 }}
        />
        <Form.Item<UserFormInstance, boolean> 
          field='active'
          label='Активен'
          component={SwitchField}
          span={{ md: 4 }}
        />
        <Form.Divider />
        <Form.Item<UserFormInstance, string> 
          field='comment'
          label='Комментарий'
          component={TextField}
          componentProps={{
            multiline: true,
          }}
          span={{ md: 12 }}
        />
      </Form>
    </Box></Paper>
  </Box>;
};

export const UserForm = memo(UserFormComponent);

